import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/shadcn/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../shadcn/dialog";
import { ScrollArea } from "@/components/shadcn/scroll-area";
import { cn } from "@/lib/utils";
import { isInsideGenieUi } from "@/lib/genie/stage-container";

export default function BaseDialog({
  children,
  title,
  description,
  descriptionClassName,
  footerNote,
  footerActions,
  footerLeft,
  footerContent,
  isOpen,
  onOpenChange,
  disableScroll = false,
  size = "xl",
  canExpand = false,
  isExpanded = false,
  onToggleExpand,
  overflowVisible = false,
  closable = true,
  stopPropagation = false,
  compact = false,
  className,
  onEscapeKeyDown,
  modal,
  anchor = "center",
}: {
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  description?: string;
  /** Overrides for the description line — `max-w-none` to let it run the full
   *  width of the dialog instead of DialogDescription's 60ch reading measure. */
  descriptionClassName?: string;
  footerNote?: string;
  footerLeft?: React.ReactNode;
  /** Replaces the footer contents while retaining the standard footer layout. */
  footerContent?: React.ReactNode;
  footerActions?: {
    label: string | React.ReactNode;
    props?: any;
  }[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  size?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "full";
  disableScroll?: boolean;
  canExpand?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  closable?: boolean;
  modal?: boolean;
  overflowVisible?: boolean;
  stopPropagation?: boolean;
  compact?: boolean;
  className?: string;
  /** `top` pins the panel near the top of the viewport so a body whose height
   *  changes while open (collapsible sections, growing tables) only extends
   *  downward instead of re-centring and shifting the header. */
  anchor?: "center" | "top";
  // Radix closes on Escape before the consumer's onOpenChange guard can
  // hold the dialog open, and the nested confirmation it opens is
  // dismissed by the same keypress. Intercept to own that decision.
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
}) {
  const handleOpenChange = (open: boolean) => {
    if (!closable && !open) {
      // Prevent closing if not closable
      return;
    }
    onOpenChange(open);
  };

  // Header/footer separators only earn their place when the body actually
  // scrolls (sticky-header pattern); compact dialogs render without them.
  const bodyRef = useRef<HTMLDivElement>(null);
  const bodyInnerRef = useRef<HTMLDivElement>(null);
  const [bodyOverflows, setBodyOverflows] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    const body = bodyRef.current;
    const inner = bodyInnerRef.current;
    if (!body || !inner) return;
    const update = () =>
      setBodyOverflows(body.scrollHeight > body.clientHeight + 1);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(body);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [isOpen]);
  const showSeparators = bodyOverflows && !overflowVisible;

  return (
    <Dialog onOpenChange={handleOpenChange} open={isOpen} modal={modal}>
      <DialogContent
        size={size}
        stopPropagation={stopPropagation}
        className={cn(
          "bg-background flex flex-col max-h-[90%]",
          overflowVisible && "overflow-visible",
          anchor === "top" && "top-[8%] translate-y-0 max-h-[84%]",
          className
        )}
        canExpand={canExpand}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        showCloseButton={closable}
        onEscapeKeyDown={onEscapeKeyDown}
        onPointerDownOutside={(event) => {
          if (isInsideGenieUi(event.target)) {
            event.preventDefault();
            return;
          }
          if (!closable) {
            event.preventDefault();
            return;
          }
          onOpenChange(false);
        }}
      >
        <DialogHeader className={cn("shrink-0", !compact && "pb-2")}>
          <DialogTitle className="leading-normal">{title}</DialogTitle>
          {description && (
            <DialogDescription className={descriptionClassName}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        {showSeparators && <div className="h-px w-full bg-border shrink-0" />}

        {overflowVisible || disableScroll ? (
          <div
            ref={bodyRef}
            className={cn(
              "flex-1 min-h-0 px-5 py-4",
              overflowVisible ? "overflow-visible" : "overflow-y-hidden"
            )}
          >
            <div ref={bodyInnerRef}>{children}</div>
          </div>
        ) : (
          <ScrollArea
            viewportRef={bodyRef}
            constrainWidth
            className="flex min-h-0 flex-1 flex-col"
            viewportClassName="h-auto! min-h-0 flex-1"
          >
            <div ref={bodyInnerRef} className="px-5 py-4">
              {children}
            </div>
          </ScrollArea>
        )}

        {footerContent !== undefined ? (
          <DialogFooter className="flex justify-end items-center shrink-0">
            {footerContent}
          </DialogFooter>
        ) : footerNote || footerActions?.length || footerLeft ? (
          <BaseDialogFooter
            footerActions={footerActions}
            footerNote={footerNote}
            footerLeft={footerLeft}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export const BaseDialogFooter = ({
  footerActions,
  footerNote,
  footerLeft,
}: {
  footerActions?: {
    label: string | React.ReactNode;
    props?: any;
  }[];
  footerNote?: string | React.ReactNode;
  footerLeft?: React.ReactNode;
}) => {
  return (
    <DialogFooter className={cn("flex justify-end items-center shrink-0")}>
      {footerNote && (
        <span className="text-foreground mr-auto text-sm font-medium">
          {footerNote}
        </span>
      )}

      {footerLeft && (
        <div className="flex gap-4 items-center h-fit mr-auto ">
          {footerLeft}
        </div>
      )}

      {footerActions && (
        <div className="flex gap-2 items-center h-fit">
          {footerActions.map((button, index) => (
            <Button key={`footer-action-${index}`} {...button.props}>
              {button.label}
            </Button>
          ))}
        </div>
      )}
    </DialogFooter>
  );
};
